import type { PaymentProvider, ProviderConfig } from '../types/common/payment'

type ProviderConfigByMode = {
  test?: ProviderConfig & Record<string, unknown>
  live?: ProviderConfig & Record<string, unknown>
}

/** Mode-specific credentials (one branch of test/live, or a flat public `providerConfig` blob). */
function isProviderConfigSatisfied(providerType: string, config: Record<string, unknown>): boolean {
  switch (providerType) {
    case 'stripe':
      return !!config.publishableKey
    case 'nmi':
    case 'white-label':
    case 'square':
    case 'authorize-net':
      return !!config.merchantGatewayId
    case 'adyen':
      return !!config.clientKey
    case 'custom-provider':
      return !!config.paymentsUrl
    default:
      return false
  }
}

/** Effective gateway types that support Save Card Details tokenization (white-label resolves to this). */
export const SAVE_CARD_SUPPORTED_EFFECTIVE_PROVIDER_TYPES = new Set(['stripe', 'nmi', 'square', 'authorize-net', 'adyen', 'white-label'])

export function getDefaultPaymentProviderEffectiveType(defaultPaymentProvider: PaymentProvider | null | undefined): string | null {
  if (!defaultPaymentProvider?.provider) return null
  const { provider, whiteLabelProvider } = defaultPaymentProvider
  if (provider === 'white-label' && whiteLabelProvider) return whiteLabelProvider
  return provider
}

export function isEffectiveProviderSupportedForSaveCard(effectiveProvider: string): boolean {
  return SAVE_CARD_SUPPORTED_EFFECTIVE_PROVIDER_TYPES.has(effectiveProvider)
}

export function isDefaultPaymentProviderSupportedForSaveCard(defaultPaymentProvider: PaymentProvider | null | undefined): boolean {
  const effective = getDefaultPaymentProviderEffectiveType(defaultPaymentProvider)
  if (!effective) return false
  return isEffectiveProviderSupportedForSaveCard(effective)
}

/** Connected-integrations API puts `paymentsUrl` on the provider root for marketplace custom apps. */
function mergeCustomProviderPaymentsUrlFromRoot(defaultPaymentProvider: PaymentProvider): PaymentProvider {
  if (defaultPaymentProvider.provider !== 'custom-provider' || Array.isArray(defaultPaymentProvider.providerConfig)) {
    return defaultPaymentProvider
  }
  const topUrl = (defaultPaymentProvider as PaymentProvider & { paymentsUrl?: string }).paymentsUrl
  if (!topUrl) return defaultPaymentProvider

  const { test, live } = defaultPaymentProvider.providerConfig as { test: ProviderConfig; live: ProviderConfig }
  return {
    ...defaultPaymentProvider,
    providerConfig: {
      test: { ...test, paymentsUrl: test.paymentsUrl ?? topUrl },
      live: { ...live, paymentsUrl: live.paymentsUrl ?? topUrl },
    },
  }
}

/**
 * Validates `defaultPaymentProvider` from **connected** integrations (`/integrations/provider/connected`, store payload).
 * Expects `providerConfig: { test, live }` (not public default-provider shape).
 */
export function isDefaultPaymentProviderConfigured(defaultPaymentProvider: PaymentProvider | null | undefined, liveMode: boolean): boolean {
  if (!defaultPaymentProvider) return false

  const merged = mergeCustomProviderPaymentsUrlFromRoot(defaultPaymentProvider)
  const { provider, providerConfig, whiteLabelProvider } = merged
  if (!provider || !providerConfig) return false

  if (provider === 'manual') return true

  if (Array.isArray(providerConfig)) return false

  const configByMode = providerConfig as ProviderConfigByMode
  if (!('test' in configByMode) || !('live' in configByMode)) return false

  const test = configByMode.test
  const live = configByMode.live
  if (test === undefined || test === null || typeof test !== 'object' || live === undefined || live === null || typeof live !== 'object') {
    return false
  }

  const config = liveMode ? live : test
  const effectiveProvider = provider === 'white-label' && whiteLabelProvider ? whiteLabelProvider : provider

  return isProviderConfigSatisfied(effectiveProvider, config as Record<string, unknown>)
}

export function resolvePaymentLiveMode(liveMode: unknown): boolean {
  if (liveMode === undefined || liveMode === null) return true
  if (typeof liveMode === 'string') return liveMode === 'true' || liveMode === '1'
  return Boolean(liveMode)
}

function isBlankPublicModeSlot(config: Record<string, unknown> | null): boolean {
  if (!config) return true
  return Object.keys(config).length === 0
}

/**
 * Validates default provider payloads from `GET .../integrations/provider/default/public`
 * (flat `providerConfig`, or `{ test, live }` with null/empty alternate mode; use after `paymentProviderFromPublicDefaultApi`).
 */
export function isPublicDefaultPaymentProviderConfigured(
  defaultPaymentProvider: PaymentProvider | null | undefined,
  liveMode?: boolean | string | null
): boolean {
  if (!defaultPaymentProvider) return false

  const { provider, providerConfig, whiteLabelProvider } = defaultPaymentProvider
  if (!provider || !providerConfig) return false

  if (provider === 'manual') return true

  if (Array.isArray(providerConfig)) return false

  const useLive = resolvePaymentLiveMode(liveMode)
  const effectiveProvider = provider === 'white-label' && whiteLabelProvider ? whiteLabelProvider : provider

  const pc = providerConfig as Record<string, unknown>
  const tRaw = pc.test
  const lRaw = pc.live
  const tObj =
    tRaw !== undefined && tRaw !== null && typeof tRaw === 'object' && !Array.isArray(tRaw) ? (tRaw as Record<string, unknown>) : null
  const lObj =
    lRaw !== undefined && lRaw !== null && typeof lRaw === 'object' && !Array.isArray(lRaw) ? (lRaw as Record<string, unknown>) : null

  if (tObj || lObj) {
    const primary = useLive ? lObj : tObj
    const secondary = useLive ? tObj : lObj
    if (primary && isProviderConfigSatisfied(effectiveProvider, primary)) return true
    if (isBlankPublicModeSlot(primary) && secondary && isProviderConfigSatisfied(effectiveProvider, secondary)) return true
    return false
  }

  return isProviderConfigSatisfied(effectiveProvider, pc)
}
